/**
 * App-wide toasts — the one feedback channel for "it worked", "it failed"
 * and "Undo".
 *
 * `ToastProvider` (mounted once in `main.tsx`, above the router) owns the
 * queue and renders the region; every component calls `useToast()`. Because
 * the provider outlives route changes, a page may show a toast and navigate
 * in the same handler — the message survives the navigation.
 */
import { createContext, useContext } from "react";

export type ToastTone = "info" | "ok" | "error";

export interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface ToastOptions {
  tone?: ToastTone;
  /** Visible time before auto-dismiss; hover or focus pauses it. */
  durationMs?: number;
  /** One inline action, typically "Undo". Clicking it dismisses the toast. */
  action?: ToastAction;
  /** Same id replaces the visible toast instead of stacking a second one. */
  id?: string;
}

export interface ToastApi {
  show: (message: string, options?: ToastOptions) => string;
  ok: (message: string, options?: Omit<ToastOptions, "tone">) => string;
  /** Error toast; an Error / API failure is reduced to its readable detail. */
  error: (error: unknown, options?: Omit<ToastOptions, "tone">) => string;
  dismiss: (id: string) => void;
}

export const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast() needs <ToastProvider> above it");
  return api;
}
